/**
 * A study's consent form is a TEMPLATE, not a finished document.
 *
 * A consent form addresses one patient ("Je soussigné(e) Jean Dupont, né(e) le
 * 3 mars 1961, consens a participer..."), but a study holds one text for a
 * whole cohort. So the authored text carries variables and the API substitutes
 * them per patient. This module is the whole contract for that: the closed list
 * of variables, the substitution, and the checks that keep a template from
 * freezing into an unfixable state at activation.
 *
 * It lives in `packages/schemas` because three workspaces need the same answer:
 * the API substitutes, the dashboard offers the list in its editor and previews
 * the result, and the activation rules are duplicated on both sides so the
 * button can say what is missing instead of surfacing a 409.
 *
 * Deliberately NOT a template engine. No conditionals, no loops, no
 * expressions: a consent form is a legal document whose text a reader has to be
 * able to predict from the source, and the moment it can branch, "what did this
 * patient actually sign" stops being answerable by reading it.
 */

/**
 * The closed vocabulary. Adding one is an API change (a resolver has to know
 * where the value comes from) plus a dashboard change (a label), which is the
 * right amount of friction: every entry here appears verbatim in a document
 * somebody signs.
 *
 * Dot paths rather than flat names so the menu can group them, and so
 * `patient.firstName` reads as what it is at the point of use.
 */
export const CONSENT_VARIABLES = [
  "patient.firstName",
  "patient.lastName",
  "patient.fullName",
  "patient.birthdate",
  "hospital.name",
  "hospital.address",
  "study.title",
  "enrolment.date",
  "today",
] as const;

export type ConsentVariable = (typeof CONSENT_VARIABLES)[number];

/** Every variable resolved to display text, in one language. */
export type ConsentVariableValues = Record<ConsentVariable, string>;

/**
 * How a resolved value is WRITTEN, shared by everything that resolves one.
 *
 * The three below live here rather than beside each caller because the API and
 * the dashboard's preview answer the same question about the same document: the
 * preview's whole job is showing what a patient will read, so a date written
 * one way on the server and another in the editor is a preview that lies about
 * the thing it exists to check. The values legitimately differ (a real patient
 * against an invented one); the formatting must not.
 *
 * `utils/consentPdf.ts` deliberately does NOT use these: its labels are French
 * only by design, so it formats in fr-FR regardless of the language the consent
 * was signed in.
 */

/** Tolerates both the API's row shape and the dashboard's settings payload. */
export type ConsentFacilityAddress = {
  street?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
};

/** One line, since it is substituted mid-sentence. */
export function formatConsentAddress(
  address: ConsentFacilityAddress | null | undefined,
): string {
  if (!address) return "";
  return [
    address.street,
    [address.postalCode, address.city].filter(Boolean).join(" "),
    address.state,
  ]
    .filter((part) => part && part.trim() !== "")
    .join(", ");
}

/**
 * `dateStyle: "long"` rather than "full": a consent form wants "3 mars 1961",
 * and the weekday a full style prepends is noise on a birthdate.
 *
 * An unusable language tag falls back to the runtime default rather than
 * throwing, and KEEPS the time zone while doing it: dropping it is how a date
 * read in UTC quietly becomes one read in the reader's own zone, which is the
 * bug the birthdate rule exists to prevent.
 */
export function formatConsentDate(
  date: Date,
  language: string,
  timeZone?: string,
): string {
  try {
    return new Intl.DateTimeFormat(language, {
      dateStyle: "long",
      timeZone,
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "long",
      timeZone,
    }).format(date);
  }
}

/**
 * A translated string in the language being rendered, else whatever has been
 * written. A consent form naming a study by a title the patient cannot read is
 * still better than one naming no study at all, and a blank entry counts as
 * unwritten: an empty string would print an empty space mid-sentence.
 *
 * NOT the dashboard's `pickTranslation`, which prefers fr then en for CHROME.
 * This one is about what goes inside the document, where a language nobody
 * asked for is a worse answer than the one language somebody wrote.
 */
export function pickConsentText(
  map: Record<string, string> | null | undefined,
  language: string | null | undefined,
): string {
  if (!map) return "";
  const exact = language ? map[language] : undefined;
  if (exact && exact.trim() !== "") return exact;
  return Object.values(map).find((text) => text && text.trim() !== "") ?? "";
}

const KNOWN = new Set<string>(CONSENT_VARIABLES);

/**
 * `{{patient.firstName}}`. The braces are the canonical form and they are what
 * the substitution matches, in the stored HTML and nowhere else.
 *
 * The dashboard's editor wraps each one in `<span data-pc-var="...">` so it can
 * round-trip the token as a single uneditable chip, but that wrapper is
 * MEANINGLESS to the matching here: a token that has lost its span resolves
 * exactly the same way. That is what keeps this a plain string operation with
 * no HTML parsing on either side.
 */
const TOKEN = /\{\{\s*([a-zA-Z][a-zA-Z0-9.]*)\s*\}\}/g;

/**
 * The same token with the editor's wrapper still around it, matched FIRST so a
 * substituted value ends up inside exactly one span instead of two nested ones.
 *
 * The braces are what decides, not the attribute: if the two ever disagreed the
 * document says what the reader sees.
 */
const WRAPPED_TOKEN =
  /<span\s+data-pc-var="[^"]*"\s*>\s*\{\{\s*([a-zA-Z][a-zA-Z0-9.]*)\s*\}\}\s*<\/span>/g;

/**
 * The class every SUBSTITUTED value carries in a rendered document.
 *
 * A resolved value is the part of a consent form a reader checks before signing
 * (their own name, their birthdate, the study they are joining) and the one part
 * the author did not type, so it is drawn apart from the body text rather than
 * blending into it. This class is the single hook the three renderers of a
 * rendered document share: the patient app's `PureRenderHTML`, the dashboard's
 * `RichTextView`, and the PDF's parser in `apps/api/src/utils/consentHtml.ts`.
 *
 * `data-pc-var` rides along beside it, read by none of them: it is what the
 * editor already wrote, and which variable a value came from is worth keeping in
 * a document somebody signed.
 */
export const CONSENT_VALUE_CLASS = "pc-value";

/** Every variable path the template mentions, in order, deduplicated. */
export function consentVariablesUsed(html: string): string[] {
  const found = new Set<string>();
  for (const match of html.matchAll(TOKEN)) found.add(match[1]!);
  return [...found];
}

/**
 * Paths the template mentions that nothing can resolve.
 *
 * The editor only ever inserts catalog entries, so these come from typing
 * braces by hand. They matter because an unresolved token is rendered VERBATIM
 * (see below) and a study freezes on activation, so this is the last moment the
 * text can be corrected.
 */
export function unknownConsentVariables(html: string): string[] {
  return consentVariablesUsed(html).filter((path) => !KNOWN.has(path));
}

/**
 * Substitute one language's text.
 *
 * Each value comes out wrapped in `CONSENT_VALUE_CLASS`, so a rendered document
 * says which of its words were filled in rather than authored, and every screen
 * that draws one can mark them without knowing what a variable is.
 *
 * An unknown path is left exactly as written, on purpose, wrapper and all.
 * Blanking it would hide a mistake inside a document somebody signs, whereas
 * `{{patinet.name}}` in the dashboard preview is unmissable, and activation
 * refuses it outright.
 */
export function renderConsentTemplate(
  html: string,
  values: ConsentVariableValues,
): string {
  const resolve = (path: string): string | null =>
    KNOWN.has(path)
      ? `<span class="${CONSENT_VALUE_CLASS}" data-pc-var="${path}">` +
        `${escapeHtml(values[path as ConsentVariable])}</span>`
      : null;

  return html
    .replace(WRAPPED_TOKEN, (whole, path: string) => resolve(path) ?? whole)
    .replace(TOKEN, (token, path: string) => resolve(path) ?? token);
}

/**
 * Values are plain text going into a markup document, so they are escaped at
 * the substitution point rather than trusted to be tame. A patient legitimately
 * named `d'Arcy & fils` must read as itself, and a facility name is operator
 * input.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Whether a language's consent form is blank to a patient.
 *
 * `.trim()` alone stopped being enough when the field became rich text: an
 * empty editor serializes to `<p></p>`, which is nine non-blank characters and
 * nothing at all on screen. A study cannot be activated without a consent form
 * in every declared language and can never be edited afterwards, so a check
 * that a blank one slips past is a study that strands its whole cohort at the
 * consent screen.
 */
export function isBlankRichText(html: string | undefined | null): boolean {
  if (typeof html !== "string") return true;
  const text = html
    // A variable is content even before it resolves.
    .replace(TOKEN, "x")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&[a-z]+;|&#\d+;/g, "x");
  return text.trim() === "";
}

/**
 * The markup a consent form may contain.
 *
 * The dashboard's editor cannot produce anything outside this list, so the
 * check is a backstop against a direct API client rather than a filter the UI
 * relies on. It REFUSES rather than sanitizes: rewriting attacker-shaped markup
 * correctly is a specialist job, refusing it is four lines, and a clinician
 * writing a consent form has no legitimate way to hit it.
 *
 * Exported because this list has three consumers that must agree and cannot
 * check each other by construction: this validator, the PDF's parser
 * (`apps/api/src/utils/consentHtml.ts`) and the editor's schema
 * (`apps/dashboard/src/components/richText/extensions.ts`). Drift is SOFT in
 * both directions (a tag the parser does not know keeps its text and loses its
 * structure; a tag the editor cannot write is simply never written), so it
 * shows up as a slightly wrong PDF rather than an error. Both sides therefore
 * iterate this object in a test.
 */
export const ALLOWED_TAGS: Record<string, readonly string[]> = {
  p: [],
  br: [],
  hr: [],
  strong: [],
  b: [],
  em: [],
  i: [],
  u: [],
  s: [],
  h1: [],
  h2: [],
  h3: [],
  ul: [],
  ol: [],
  li: [],
  blockquote: [],
  a: ["href", "target", "rel"],
  /**
   * A variable: the editor's chip while it is still a template
   * (`data-pc-var`), the value it resolved to once rendered (`class`, see
   * `CONSENT_VALUE_CLASS`). Both are listed so a RENDERED consent form is still
   * markup this allowlist accepts, which is what the snapshot in
   * `PatientStudy.consentFormSigned` is.
   */
  span: ["data-pc-var", "class"],
};

/**
 * The link schemes a consent form may carry.
 *
 * Exported because the dashboard's editor narrows TipTap's ten default schemes
 * to exactly this list: a link a clinician can type has to be a link that
 * saves, and two copies of the rule is how one of them ends up accepting
 * something the other refuses at 400.
 */
export const SAFE_HREF = /^(https?:\/\/|mailto:)/i;

/**
 * Human-readable reasons the markup is refused, empty when it is fine.
 *
 * Returned as a list rather than a boolean so the 400 can say which tag, which
 * is the difference between a clinician pasting from Word and understanding
 * why, and a clinician retyping the whole form.
 */
export function consentHtmlViolations(html: string): string[] {
  const problems = new Set<string>();

  // Anything that is not a well-formed simple tag lands here, including
  // comments, doctypes, CDATA and a `>` hidden inside an attribute value. All
  // of those are refusals rather than attempts at recovery.
  for (const token of html.match(/<[^>]*>?/g) ?? []) {
    const parsed = /^<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)\s*([^<>]*?)\/?>$/.exec(
      token,
    );
    if (!parsed) {
      problems.add(`malformed markup: ${token.slice(0, 40)}`);
      continue;
    }

    const [, closing, rawName, rawAttrs] = parsed;
    const name = rawName!.toLowerCase();
    const allowedAttrs = ALLOWED_TAGS[name];
    if (!allowedAttrs) {
      problems.add(`<${name}> is not allowed in a consent form`);
      continue;
    }
    // A closing tag carries no attributes; `</p style=...>` is malformed.
    if (closing) {
      if (rawAttrs!.trim() !== "") problems.add(`malformed closing </${name}>`);
      continue;
    }

    for (const attr of rawAttrs!.matchAll(
      /([a-zA-Z][a-zA-Z0-9-]*)\s*(?:=\s*("[^"]*"|'[^']*'|[^\s"'`=<>]+))?/g,
    )) {
      const attrName = attr[1]!.toLowerCase();
      if (!allowedAttrs.includes(attrName)) {
        problems.add(`${attrName} is not allowed on <${name}>`);
        continue;
      }
      if (attrName === "href") {
        const value = (attr[2] ?? "").replace(/^["']|["']$/g, "");
        // `javascript:` is the one that matters; the allowlist is inverted so
        // a scheme nobody thought of is refused rather than permitted.
        if (!SAFE_HREF.test(value)) {
          problems.add(`only http, https and mailto links are allowed`);
        }
      }
    }
  }

  return [...problems];
}
